import { ImageMedia, ModuleColorTheme, UmbracoAlgoliaCategories } from '~/lib';
import { FC, useMemo } from 'react';
import clsx from 'clsx';
import { Grid } from '~/shared/components/Grid/Grid';
import { Container } from '~/shared/components/Container';
import { PageHeroProps } from '~/templates/pages/components/PageHero/PageHero';
import { PLPCarousel } from './components/PLPCarousel/PLPCarousel';
import { PLPCarouselItemProps } from './components/PLPCarousel/PLPCarouselItem';
import styles from './PLPHero.module.scss';
import { TCategoryPickerItem } from '../CategoryPickerItem/CategoryPickerItem';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { PLPHeroImage } from './components/PLPHeroImage';
import { PLPHeroContent } from './components/PLPHeroContent';
import { usePage } from '~/templates/pages';
import { PLPHeroAIGuideBanner } from './components/PLPHeroAIGuideBanner';

const MAX_SHALLOW_CATEGORY_LEVEL = 1;

type UmbracoPLPHeroProps = Omit<PageHeroProps, 'headline' | 'theme' | 'imageAltText'> & {
    heroHeader?: string;
    heroTheme?: 'Light' | 'Dark';
    heroImage?: ImageMedia;
    colorTheme?: ModuleColorTheme;
    heroTrumpet?: string;
    heroWatermark?: string;
    trumpet?: string;
    heroImageAltText?: string;
    categories?: TCategoryPickerItem[];
    heroColorTheme?: ModuleColorTheme;
    showImageOnMobile?: boolean;
    dataType?: keyof typeof UmbracoAlgoliaCategories;
    enableAIProductGuideBanner?: boolean;
};

interface CustomCSSProperties extends React.CSSProperties {
    [key: `--${string}`]: string | number | undefined;
}

interface BasePLPHeroProps {
    colorTheme?: ModuleColorTheme;
    title: string;
    trumpet?: string;
    heroTrumpet?: string;
    watermark?: string;
    image?: ImageMedia;
    imageAltText?: string;
    carousel?: {
        items: PLPCarouselItemProps[];
    };
    showImageOnMobile?: boolean;
    dataType?: keyof typeof UmbracoAlgoliaCategories;
    enableAIProductGuideBanner?: boolean;
}

const Hero: FC<BasePLPHeroProps> = ({
    colorTheme,
    trumpet,
    title,
    watermark,
    carousel,
    image,
    imageAltText,
    showImageOnMobile,
    dataType,
    enableAIProductGuideBanner,
}) => {
    const isMobile = useIsMobile();
    const { breadcrumb } = usePage();

    const isCategoryPage = dataType === 'Category Id';

    // breadcrumb items count
    const categoryLevel = useMemo(() => {
        if (!isCategoryPage) return null;

        const breadcrumbItemCount = breadcrumb?.items?.length ?? 0;

        return Math.max(breadcrumbItemCount - 2, 0);
    }, [isCategoryPage, breadcrumb]);

    const isDeepCategory = categoryLevel !== null && categoryLevel > MAX_SHALLOW_CATEGORY_LEVEL;
    const isShallowCategory = categoryLevel === null || categoryLevel <= MAX_SHALLOW_CATEGORY_LEVEL;
    const { categories } = usePage();
    const currentCategory = categories?.find((item) => item.isActive);

    const isPillVariant = currentCategory?.isActive;

    const themeVariables = useMemo<CustomCSSProperties>(
        () => ({
            '--color-main': colorTheme?.mainColor,
            '--color-secondary': colorTheme?.secondaryColor,
            '--color-foreground': colorTheme?.foregroundColor,
            '--watermark-opacity': colorTheme?.secondaryColorOpacity,
            '--watermark-color': colorTheme?.highlightColor,
        }),
        [colorTheme],
    );

    const isShowImage = useMemo(() => {
        if (!image) return false;

        // Category level 2-4: never show a hero image, on any device
        if (isDeepCategory) return false;

        // Return false if on mobile and showImageOnMobile is false
        if (isMobile && !showImageOnMobile) return false;

        // Return true in all other cases
        return true;
    }, [image, showImageOnMobile, isMobile, isDeepCategory]);

    if (enableAIProductGuideBanner) {
        return (
            <>
                <PLPHeroAIGuideBanner title={title} />

                {!!carousel?.items.length && (
                    <Container className={styles.carouselSection} hasGutter>
                        <PLPCarousel items={carousel.items} isPillVariant={isPillVariant} />
                    </Container>
                )}
            </>
        );
    }

    return (
        <>
            <div
                className={clsx(
                    styles.plpHero,
                    { [styles.noHeroImage]: !isShowImage },
                    { [styles.noHeroImageOnMobile]: !showImageOnMobile },
                    { [styles.plpHeroCategory]: isCategoryPage },
                    { [styles.plpHeroSmall]: isShallowCategory && isShowImage },
                    { [styles.plpHeroTitleOnly]: isDeepCategory },
                )}
                style={themeVariables}
            >
                <Container className={styles.container} fullWidth>
                    <Grid className={styles.grid} flow="row" columns="1fr" rowGap="large">
                        {image && isShowImage && (
                            <PLPHeroImage image={image} imageAltText={imageAltText}></PLPHeroImage>
                        )}

                        <PLPHeroContent
                            hasImage={isShowImage}
                            hasWaterMark={isShallowCategory}
                            title={title}
                            trumpet={trumpet}
                            watermark={watermark}
                        />
                    </Grid>
                </Container>
            </div>

            {!!carousel?.items.length && (
                <Container className={styles.carouselSection} hasGutter>
                    <PLPCarousel items={carousel.items} isPillVariant={isPillVariant} />
                </Container>
            )}
        </>
    );
};

/**
 * This helps adapt our base Hero's props with the data from Umbraco.
 */
const HeroAdapter: FC<UmbracoPLPHeroProps> = (props) => {
    const {
        heroHeader,
        categories,
        image,
        heroTrumpet,
        heroWatermark,
        heroColorTheme,
        heroImageAltText,
        showImageOnMobile,
        dataType,
        enableAIProductGuideBanner,
    } = props;

    const carousel = useMemo(() => {
        if (!categories?.length) return undefined;

        const items = categories.map<PLPCarouselItemProps>((category) => ({
            text: category.link.text || category.link.title || '',
            isActive: category.isActive,
            href: category.link.url || '',
            image: category.icon,
        }));

        return { items };
    }, [categories]);

    return (
        <Hero
            title={heroHeader ?? ''}
            carousel={carousel}
            trumpet={heroTrumpet}
            image={image}
            watermark={heroWatermark}
            colorTheme={heroColorTheme}
            imageAltText={heroImageAltText}
            showImageOnMobile={showImageOnMobile}
            dataType={dataType}
            enableAIProductGuideBanner={enableAIProductGuideBanner}
        />
    );
};

export type PLPHeroProps = UmbracoPLPHeroProps;

export const PLPHero: FC<PLPHeroProps> = ({ dataType, ...props }) => {
    // On Brand pages, we will not show tiles in hero
    const isBrandPage = dataType === 'Brand Id';

    return <HeroAdapter {...props} dataType={dataType} showImageOnMobile={isBrandPage} />;
};
